import React from 'react';
import { useLocale } from './index';

export function localizedMoney(amount: number, english: string | number, language: 'en' | 'de'): string {
  if (language === 'en' || !Number.isFinite(amount)) return String(english);
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'USD' }).format(amount);
}

// The API returns US numeric dates or abbreviated English month names, not ISO dates.
// Parse that explicit contract without changing the stored value used by billing logic.
export function localizedBillingDate(value: string | null, language: 'en' | 'de'): string {
  if (!value) return '';
  if (language === 'en') return value;
  if (value === 'No billing') return 'Keine Abrechnung';
  const numeric = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  const named = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2})(?:, (\d{4}))?$/.exec(value);
  if (!numeric && !named) return value;
  const month = numeric ? Number(numeric[1]) - 1 : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].indexOf(named![1]);
  const day = Number(numeric ? numeric[2] : named![2]);
  const year = Number(numeric ? numeric[3] : named![3] || 2000);
  const date = new Date(Date.UTC(year, month, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) return value;
  return new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: named && !named[3] ? 'long' : '2-digit',
    ...(numeric || named?.[3] ? { year: 'numeric' as const } : {}), timeZone: 'UTC' }).format(date);
}

export function Money({ amount, english }: { amount: number; english: string | number }) {
  const { language } = useLocale();
  return <>{localizedMoney(amount, english, language)}</>;
}

export function localizedConversationDate(value: string, language: 'en' | 'de'): string {
  const date = new Date(value);
  // Keep the viewer's timezone and the existing English formatting behavior.
  if (language === 'en') return date.toLocaleDateString();
  if (!Number.isFinite(date.getTime())) return 'Datum unbekannt';
  return date.toLocaleDateString('de-DE');
}

export function ConversationDate({ value }: { value: string }) {
  const { language } = useLocale();
  return <>{localizedConversationDate(value, language)}</>;
}

export function BoosterPrice({ amount }: { amount: number }) {
  const { language } = useLocale();
  return <>{language === 'de' ? `Booster für ${localizedMoney(amount, '', 'de')}` : `Boost for $${amount}`}</>;
}

export function Discount({ amount }: { amount: number }) {
  const { language } = useLocale();
  return <>{language === 'de' ? `${amount} % Rabatt` : `$${amount}% off`}</>;
}
